// src/components/Shell.tsx
//
// The app shell: SGDS's own government trust bar, a PSD-navy brand band with a
// TEXT wordmark (no crest, no logo raster — sgds-reconciliation.md §3a: the
// only artwork on hand is a 320px PNG scraped from a public site, and the
// state crest is a protected emblem regardless), a left side navigation
// (mandated: C-10(d) "Menu Side Bar"), and a toolbar carrying the signed-in
// identity, active role, and the appearance toggle.
//
// ══ TASK 5f — THE DEMONSTRATION/SYNTHETIC-DATA NOTICE IS GONE FROM HERE ══
//
// Until this task, the brand band carried a badge and a collapsible
// `SgdsAlert` (Task 5a/5e: `demonstrationBanner()`/`combinedNotice()` in
// `lib/environment.ts`) on every route this shell wraps. The Product Owner
// decided that no clause in the tender requires an on-screen synthetic-data
// or demonstration notice, and that the Public Service Division must see the
// production view as closely as possible — so the badges and the alert are
// DELETED here, not hidden, and `Shell.dom.test.tsx` proves the absence
// across the same server-report matrix the old tests drove.
//
// This removes screen chrome, not a control. Nothing below is what actually
// carries the honesty; that is entirely server-side and untouched:
//   * The gateway still refuses to boot Demonstration Mode beside a
//     non-local outbound channel or a settlement dispatch other than
//     `'none'` (`assertDemonstrationModeSafe`, `services/gateway/src/server.ts`).
//   * `PRS2_DEMONSTRATION_MODE` still defaults off and is enabled only by
//     the exact string `'on'`.
//   * `audit.event` still tags every row with `demonstration_mode` at
//     INSERT, immutable thereafter (`009_ledger_immutability.sql`).
//   * `/api/identities`, `/api/session` and `/api/me` still report
//     `demonstrationMode`, `identityProvider` and `settlementDispatch` — a
//     client may still ask; this component just no longer renders a claim
//     from the answer.
//   * `assertDemoIdentityAllowed` still refuses an unrecognised `APP_ENV`.
// See `docs/security/SECURITY.md` S-016 for how this closes that finding.
//
// `apps/officer-web/src/pages/LoginPage.tsx` still derives a badge and a
// DANGER-only alert from `environmentBanner()` in `lib/environment.ts` — a
// genuine anomaly alarm for the one screen offering a passwordless sign-in,
// not the reassurance notice this file used to also render. That is
// deliberately untouched by this task; see that page's own comment.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import {
  mayEmitReprorationAlert,
  mayReadFullAuditTrail,
  mayReprorateBills,
  ROLE_LABELS,
  mayAccessFinancialCounselling,
  mayPrintCsCard,
  mayRaiseSupportTicket,
  mayReadAccountDirectory,
  mayReadUserAccounts,
  mayReadAgencyMappings,
  mayReadAnnouncements,
  mayReadEmailTemplates,
  mayReadExclusionList,
  mayReadHspGroupMemberships,
  mayReadHspGroups,
  mayReadMbimRecords,
  mayReadMedicalSchemeCodes,
  mayReadNotificationRules,
  mayReadSpecialAgencyCases,
  mayReadIdentityProviders,
  mayReadSubsystems,
  mayAdministerReferenceData,
  mayReadWardClasses,
  mayUseFaq,
  mayUseHelpAndManual,
  readableReferenceParameters,
  type Scope,
} from '@prs2/auth'
import { SgdsButton, SgdsMasthead } from '@govtechsg/sgds-web-component/react'
import { CrumbContext, pageTitle, type CrumbRegistration } from '../lib/crumbs'
import { useAuth } from '../state/AuthContext'
import { useTheme } from '../state/ThemeContext'
import { AgencyMark } from './AgencyMark'
import { GovFooter } from './GovFooter'

// ★ The rail is arranged by WHAT THE OFFICER IS TRYING TO DO, not by who is allowed in. It used to be
// three groups, one of them a flat list of about 23 administration links in the order they were
// built. Seven groups of at most ten links each, named for the job, mean a PSD officer scans a
// section rather than a column, and a role that sees few links sees few headings (a group with no
// visible link is not rendered). Display order within a group is NAV_ORDER, below.
export const NAV_GROUPS = [
  'Operations',
  'Reports and audit',
  'Rules and rates',
  'Providers and agencies',
  'Access and security',
  'Communications',
  'Support',
] as const
type NavGroup = (typeof NAV_GROUPS)[number]

/**
 * ★★ THE RAIL IS ROLE-GATED, AND IT GATES WITH THE SERVER'S OWN PREDICATE
 * (2026-08-18).
 *
 * ⚠ **THE DEFECT.** Every signed-in officer was offered all twenty-eight links,
 * including the eleven PSD-only administration catalogues. A `PAYMENT_USER`
 * clicking *Class of wards* got a refusal from a route that was right to refuse
 * — so the product spent a click telling a user it had lied to them a moment
 * earlier, twenty-eight times over. `docs/HANDOFF.md` §5 item 6 carried this
 * from 2026-08-16 as a Wave 5 UI defect. It is also a small disclosure: the
 * unfiltered rail is a map of PSD's entire administration surface handed to
 * every counterparty user who signs in.
 *
 * ★ **AND THE THING THAT MAKES IT SAFE IS THAT NO RULE IS RESTATED HERE.**
 * `visibleTo` holds THE SAME EXPORTED FUNCTION the gateway route's own guard
 * calls — `mayReadWardClasses` is one function in `@prs2/auth`, called by
 * `routes/ward-classes.ts` and by this file. A client-side copy of an
 * authorisation rule is the thing this repository refuses on principle (S-011's
 * shape, and `ENGINEERING.md` §2's server-authoritative invariant): two statements of
 * one sentence drift, and the one that drifts silently is always the client's.
 * Hiding a link is **presentation**, never enforcement — every route still
 * refuses independently, and `S-057` is what it costs when a refusal is not a
 * refusal.
 *
 * ⚠ **A link with no `visibleTo` is deliberately open to every signed-in role**,
 * because its SERVER route is: the Operations surfaces scope their DATA to the
 * caller rather than refusing the page (a `HSP_USER` reading Bills sees its own
 * bills), and hiding those would remove the officer's own work from their own
 * navigation. Absent is therefore "open", not "unclassified" —
 * `Shell.dom.test.tsx` asserts the distinction per role so a new link cannot
 * inherit openness by being forgotten.
 */
type NavVisibility = (scope: Scope) => boolean

export const NAV_LINKS: readonly { to: string; label: string; group: NavGroup; visibleTo?: NavVisibility }[] = [
  { to: '/dashboard', label: 'Processing overview', group: 'Operations' },
  { to: '/bills', label: 'Bills', group: 'Operations' },
  { to: '/entitlement', label: 'Entitlement enquiry', group: 'Operations' },
  { to: '/approvals', label: 'Approval queue', group: 'Operations' },
  // ★★ WAVE 1a — Immediately before Records because the two are
  // one journey: send a delivery, then read what happened to each record in it.
  { to: '/upload', label: 'Upload a delivery', group: 'Operations' },
  { to: '/records', label: 'Records', group: 'Operations' },
  { to: '/contacts', label: 'Contacts', group: 'Operations' },
  // ★ PHASE 10 (Task 37) — Annex A 5/6/8, read-only.
  { to: '/settlement-batches', label: 'Settlement batches', group: 'Operations' },
  // ★★ WAVE 2b — DELIBERATELY UNGATED, by the rule the audit
  // link's own comment above establishes: GET /api/reports/custom and its
  // catalogue and preview siblings are guarded by `requireSession` ALONE, and
  // every row they return is narrowed by `billScopePredicate` to the bills this
  // officer may already see. The route never answers 403, so hiding the link
  // would take a surface away from an officer entitled to it — it would just
  // report on a smaller population, which is the whole point of the predicate.
  { to: '/reports/custom', label: 'Custom report', group: 'Reports and audit' },
  // ⚠ DELIBERATELY UNGATED, and it was gated on `mayReadFullAuditTrail` for
  // about ten minutes while this change was written — which would have been a
  // REGRESSION, not a fix. `GET /api/audit` (routes/bills.ts) is guarded by
  // `requireSession` ALONE: every signed-in officer may read the trail, narrowed
  // by `PostgresAuditRepository.list` to THEIR OWN rows unless
  // `mayReadFullAuditTrail` widens it. So the predicate governs HOW MUCH of the
  // page is populated, never whether the officer may open it, and hiding the
  // link would have removed every non-privileged officer's own audit history
  // from their own navigation.
  //
  // ★ The general rule this case establishes, and the reason it is written here
  // rather than in a commit message: gate a link on a predicate ONLY where the
  // route answers 403 to it. Where the route narrows DATA instead, the page is
  // the officer's to open and the rail must offer it.
  { to: '/audit', label: 'Audit trail', group: 'Reports and audit' },
  // ★★ WAVE 9 — P2C1 §12.134/§12.135. GATED, unlike the trail above it, and the
  // difference is exactly the rule the trail's own comment establishes: gate a link
  // on a predicate ONLY where the route answers 403 to it. All four archive routes
  // answer 403 to anything without `mayReadFullAuditTrail` — they narrow no data,
  // they refuse outright — so a link offered to an HSP officer would open a page of
  // four refusals.
  {
    to: '/audit/archive',
    label: 'Audit archive',
    group: 'Reports and audit',
    visibleTo: mayReadFullAuditTrail,
  },
  // ★★★ WAVE 13 (P10) — P2C1 §12.141's retention-standard limb. Beside the
  // archive because they are two limbs of one clause family and share an
  // authorisation gate; separate because one is an operator console over
  // rotation and the other is a compliance register of what must be kept.
  {
    to: '/audit/retention',
    label: 'Retention standard',
    group: 'Reports and audit',
    visibleTo: mayReadFullAuditTrail,
  },
  // ★★★ WAVE 14 — P2C1 §12, Recovery Agency row 7. The re-proration sweep and
  // the alert file it feeds.
  //
  // ⚠ **Gated on the SAME capability as the two above, and it is not the obvious
  // one.** Re-prorating is a billing operation, not an audit one, so the reflex
  // is a billing gate. Both routes actually check `mayReadFullAuditTrail`, for
  // the reason the sweep route states: a sweep reads every bill in scope and
  // re-divides money on any of them. The link must agree with the route or an
  // officer follows it to a 403 — so the predicate is the route's, restated
  // nowhere.
  //
  // ⚠ In **Administration** rather than Operations, beside the archive and the
  // retention register, because the population it acts on is the whole estate
  // rather than the bills an officer is working through. Its path is under
  // `/operations/` all the same: the URL names the operation and the rail names
  // where an operator looks for it, and neither is the other's label.
  {
    to: '/operations/reproration',
    label: 'Re-proration console',
    group: 'Operations',
    // ★★ The screen's TWO controls answer to two predicates since S-084, so the
    // link is offered when EITHER is held — not when the audit-trail capability
    // is, which is what it read until 2026-08-23. A rail entry gated on a
    // predicate its routes no longer enforce is the shape this rail's standing
    // rule exists to refuse: an officer follows the link to a 403, or never sees
    // a screen they may in fact use.
    visibleTo: (scope) => mayReprorateBills(scope) || mayEmitReprorationAlert(scope),
  },
  // ★ PHASE 10 (Task 35) — Annex A 40-47, read-only.
  //
  // ⚠ Gated on "may read AT LEAST ONE of the nine parameters", DERIVED from the
  // same `REFERENCE_READ_ROLES` table the route reads, never restated. Six of
  // the eight human roles appear in that table for at least one parameter;
  // `ENTITLEMENT_USER` and `MMBA_USER` appear for NONE, so for exactly those two
  // this page could only ever have rendered an empty catalogue — and an empty
  // catalogue reads as "there is no reference data", which is false. This is the
  // one link whose predicate is a fold rather than a single flag, because the
  // route's own answer is per-parameter (403 each) rather than per-page.
  {
    to: '/reference-data',
    label: 'Reference data',
    group: 'Rules and rates',
    visibleTo: (scope) => readableReferenceParameters(scope).length > 0,
  },
  // ★ PHASE 6 (Tasks 18/19) — // ★★ ADR 0011 — Annex A 52-59, the first link over them. GATED, because
  // `/api/administration/accounts*` answers a non-PSD officer 403 — the rule this
  // rail follows is: gate a link only where the route refuses, and this one does.
  {
    to: '/administration/accounts',
    label: 'Accounts and groups',
    group: 'Access and security',
    visibleTo: mayReadAccountDirectory,
  },
  // User account administration: the System Administrator's own screen. Gated on the SAME predicate the routes gate on, so a
  // PSD officer is not shown a link to a screen that would refuse them.
  {
    to: '/administration/users',
    label: 'User accounts',
    group: 'Access and security',
    visibleTo: mayReadUserAccounts,
  },
  // ★★ Annex A story 17 — the access-control matrix. GATED on the SAME capability
  // the route gates on (`mayReadFullAuditTrail`), which is the trail's own: this
  // page enumerates what every role may do and what authority every account holds,
  // and that is treated as sensitive as the trail itself.
  {
    to: '/administration/access-control',
    label: 'Access control matrix',
    group: 'Access and security',
    visibleTo: mayReadFullAuditTrail,
  },
  { to: '/administration/ward-classes', label: 'Class of wards', group: 'Rules and rates', visibleTo: mayReadWardClasses },
  // ★★★ WAVE 13 (P8) — Annex A 19-21's ward SUBSIDY RATES. Beside the ward-class
  // catalogue and deliberately a separate entry: that one is classification and
  // holds no rate, this one is the money a CPW/FAW/CWM bill is priced by. Gated
  // on `mayAdministerReferenceData`, which is what the route enforces — the rule
  // this file states is that a link is gated only where the route answers 403.
  {
    to: '/administration/ward-rules',
    label: 'Ward subsidy rates',
    group: 'Rules and rates',
    visibleTo: mayAdministerReferenceData,
  },
  // ★★ ANNEX A 22 - the MSO pro-ration rule engine. Gated on `mayAdministerReferenceData`, the
  // predicate every `/api/mso-rules` and `/api/mso-calculations` route calls: each answers 403 to a
  // non-PSD caller, so the rail offers the link only where the route would answer.
  {
    to: '/administration/mso-rules',
    label: 'MSO pro-ration rules',
    group: 'Rules and rates',
    visibleTo: mayAdministerReferenceData,
  },
  { to: '/mso/calculator', label: 'MSO calculator', group: 'Rules and rates', visibleTo: mayAdministerReferenceData },
  { to: '/mso/calculations', label: 'MSO calculation enquiry', group: 'Rules and rates', visibleTo: mayAdministerReferenceData },
  // Q-54: the decision screen. PSD only, the predicate every MSO route calls.
  { to: '/mso/comparison', label: 'MSO engine comparison', group: 'Rules and rates', visibleTo: mayAdministerReferenceData },
  // ★★★ WAVE 12 (P2) — Annex A story 14's configuration module. PSD-only, like
  // every sibling catalogue, and gated on its OWN predicate rather than reusing a
  // neighbour's: this surface decides which sign-in doors exist.
  {
    to: '/administration/identity-providers',
    label: 'Identity providers',
    group: 'Access and security',
    visibleTo: mayReadIdentityProviders,
  },
  { to: '/administration/subsystems', label: 'Sub-systems', group: 'Access and security', visibleTo: mayReadSubsystems },
  { to: '/administration/agency-mappings', label: 'RA/PA mapping', group: 'Providers and agencies', visibleTo: mayReadAgencyMappings },
  // ★ PHASE 6 (Task 20).
  { to: '/administration/medical-scheme-codes', label: 'Medical scheme codes', group: 'Rules and rates', visibleTo: mayReadMedicalSchemeCodes },
  // ★ PHASE 6 (Task 21) — Annex A stories 29/30.
  { to: '/administration/hsp-groups', label: 'HSP groups', group: 'Providers and agencies', visibleTo: mayReadHspGroups },
  { to: '/administration/hsp-group-memberships', label: 'HSP group membership', group: 'Providers and agencies', visibleTo: mayReadHspGroupMemberships },
  // ★ PHASE 6 (Task 22).
  { to: '/administration/mbim-records', label: 'MBIM records', group: 'Providers and agencies', visibleTo: mayReadMbimRecords },
  // ★ PHASE 6 (Task 23).
  { to: '/administration/exclusion-list', label: 'Exclusion list', group: 'Providers and agencies', visibleTo: mayReadExclusionList },
  // ★ PHASE 6 (Task 24).
  { to: '/administration/email-templates', label: 'Email templates', group: 'Communications', visibleTo: mayReadEmailTemplates },
  // ★ PHASE 6 (Task 25).
  { to: '/administration/announcements', label: 'Announcements', group: 'Communications', visibleTo: mayReadAnnouncements },
  // ★ PHASE 6 (Task 26).
  { to: '/administration/special-agency-cases', label: 'Special agencies/cases', group: 'Providers and agencies', visibleTo: mayReadSpecialAgencyCases },
  // ★ PHASE 10 — Annex A story 50, the notification RECORD and its trigger.
  { to: '/notifications', label: 'eCS notifications', group: 'Communications', visibleTo: mayReadNotificationRules },
  // ★ PHASE 10 — Annex A story 51, financial counselling cost estimation.
  { to: '/cost-estimate', label: 'Financial counselling', group: 'Support', visibleTo: mayAccessFinancialCounselling },
  // ★ PHASE 10 — Annex A stories 24/25, online help and the user manual.
  { to: '/help', label: 'Help & user manual', group: 'Support', visibleTo: mayUseHelpAndManual },
  // ★ PHASE 11 — Annex A story 39, the scoped FAQ.
  //
  // ★★ WAVE 8 (Task 5) — LABELLED 'FAQ keyword search', not 'FAQ' and never
  // 'Chatbot'. Annex A story 39 is titled "General FAQ or Enquiry Chatbot" and
  // that title stays quoted wherever the code cites the requirement; the label
  // an officer reads names what the feature actually is. The PATH is unchanged
  // — a rename that breaks a saved link is a different kind of change.
  { to: '/faq', label: 'FAQ keyword search', group: 'Support', visibleTo: mayUseFaq },
  // ★ PHASE 11 — Annex A story 48, local support tickets.
  { to: '/support-tickets', label: 'Support tickets', group: 'Support', visibleTo: mayRaiseSupportTicket },
  /**
   * ★★ WAVE 10 — referrals and paused bills (Annex A 37/38).
   *
   * ⚠ **NO `visibleTo`, deliberately, and this is the one nav decision in Wave 10
   * worth arguing.** Every route behind this link is open to any signed-in officer:
   * `GET /api/bills/referred-to-me` answers for whoever asks, scope-filtered, and
   * returns an empty list to somebody nobody has referred anything. There is no
   * capability to gate on, and gating on "holds an approval grant" would hide the
   * inbox from a level-0 reader a colleague had legitimately asked to look at
   * something — which is exactly the reader a referral is most useful for.
   *
   * ⚠ In `Operations` rather than `Support`: this is work, not help.
   */
  { to: '/attention', label: 'Your attention', group: 'Operations' },

  // ★ PHASE 10 — Annex A story 49, print CS card information (Ministry/Agency HR only).
  { to: '/cs-card', label: 'Print CS card', group: 'Support', visibleTo: mayPrintCsCard },
]

/**
 * The display order of the rail, top to bottom within each group (groups follow NAV_GROUPS). Most
 * frequent and most urgent first: "Your attention" sits directly under the overview because it is
 * the "what needs me" entry, and the approval queue and bills follow it. Every link in NAV_LINKS must
 * appear here exactly once: `Shell.dom.test.tsx` fails a link that is not placed.
 */
export const NAV_ORDER: readonly string[] = [
  '/dashboard',
  '/attention',
  '/approvals',
  '/bills',
  '/entitlement',
  '/records',
  '/upload',
  '/settlement-batches',
  '/operations/reproration',
  '/contacts',
  '/reports/custom',
  '/audit',
  '/audit/archive',
  '/audit/retention',
  // Reference data leads "Rules and rates": it is the catalogue every other rule and rate reads from.
  '/reference-data',
  '/administration/mso-rules',
  '/mso/calculator',
  '/mso/calculations',
  '/mso/comparison',
  '/administration/ward-rules',
  '/administration/ward-classes',
  '/administration/medical-scheme-codes',
  '/administration/hsp-groups',
  '/administration/hsp-group-memberships',
  '/administration/agency-mappings',
  '/administration/mbim-records',
  '/administration/special-agency-cases',
  '/administration/exclusion-list',
  '/administration/accounts',
  '/administration/users',
  '/administration/access-control',
  '/administration/identity-providers',
  '/administration/subsystems',
  '/administration/announcements',
  '/administration/email-templates',
  '/notifications',
  '/help',
  '/faq',
  '/support-tickets',
  '/cost-estimate',
  '/cs-card',
]
const navRank = (to: string): number => NAV_ORDER.indexOf(to)

/** Stable, collision-free ids for each group's `aria-labelledby` pairing (a slug: ids may not hold spaces). */
const groupHeadingId = (group: NavGroup): string => `pt-rail-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

export const CRUMB_LABEL: Record<string, string> = {
  dashboard: 'Processing overview',
  bills: 'Bills',
  entitlement: 'Entitlement enquiry',
  approvals: 'Approval queue',
  upload: 'Upload a delivery',
  records: 'Records',
  contacts: 'Contacts',
  audit: 'Audit trail',
  // ★★ WAVE 10 — without this the breadcrumb renders the raw segment 'attention'.
  attention: 'Your attention',
  // ★★ WAVE 9 — the /audit/archive crumb. Without it the breadcrumb renders the
  // raw segment 'archive', which is the drift this map exists to prevent.
  archive: 'Audit archive',
  retention: 'Retention standard',
  administration: 'Administration',
  'ward-classes': 'Class of wards',
  'ward-rules': 'Ward subsidy rates',
  'identity-providers': 'Identity providers',
  subsystems: 'Sub-systems',
  'agency-mappings': 'RA/PA mapping',
  'medical-scheme-codes': 'Medical scheme codes',
  'hsp-groups': 'HSP groups',
  'hsp-group-memberships': 'HSP group membership',
  'mbim-records': 'MBIM records',
  'exclusion-list': 'Exclusion list',
  'email-templates': 'Email templates',
  announcements: 'Announcements',
  'special-agency-cases': 'Special agencies/cases',
  'reference-data': 'Reference data',
  'settlement-batches': 'Settlement batches',
  'cost-estimate': 'Financial counselling',
  help: 'Help & user manual',
  'cs-card': 'Print CS card',
  notifications: 'eCS notifications',
  faq: 'FAQ keyword search',
  'support-tickets': 'Support tickets',

  // ★★ WAVE 24 — the six segments that had NO entry here, so four screens rendered a
  // raw URL segment in the breadcrumb: "Home / operations / reproration" on the
  // Wave 14 flagship, and the same on /reports/custom, /administration/accounts
  // and /administration/access-control. `crumb-labels.dom.test.ts` now fails if a
  // route gains a segment this map does not carry — the map's own comments above
  // say it exists to prevent exactly this drift, and until now nothing enforced it.
  //
  // ⚠ `operations` and `reports` are PATH PREFIXES, not routes. They are labelled
  // because the breadcrumb renders every segment, so the prefix is on screen even
  // though nothing navigates to it.
  operations: 'Operations',
  reproration: 'Re-proration console',
  reports: 'Reports',
  custom: 'Custom report',
  accounts: 'Accounts and groups',
  users: 'User accounts',
  assume: 'Switch user',
  'access-control': 'Access control matrix',
  // ANNEX A 22 - the MSO rule engine. `versions` is a path word, the id after it is the crumb.
  'mso-rules': 'MSO pro-ration rules',
  mso: 'MSO',
  calculator: 'MSO calculator',
  calculations: 'MSO calculation enquiry',
  comparison: 'MSO engine comparison',
  versions: 'Versions',
}

/**
 * Appearance toggle — light or dark.
 *
 * Replaces the two debug controls that were here ("Theme: system", "Flatten: off").
 * Neither belonged in the product: `Flatten` was a design-verification gate that
 * switches every shadow off to prove nothing depends on softness to be findable,
 * and it still lives in `docs/design/mockups/dashboard-direction.html` where a
 * reviewer needs it. Shipping a verification control to an officer processing
 * medical bills invites them to press it and wonder what broke.
 *
 * Two states. `system` survives as the INITIAL state — a user whose operating
 * system is dark still gets dark on first load without asking — but it is not a
 * stop on the control, because a button showing a sun means "make it light", not
 * "advance to the next of three things".
 *
 * The icon is inline SVG with `aria-hidden`; the button carries the accessible name
 * and `aria-live` announces the change, so the control is not colour-and-glyph only.
 */
function AppearanceToggle() {
  const { isDark, toggleAppearance } = useTheme()

  // The icon shows what pressing it WILL DO, not what is currently on. A sun on a
  // dark screen reads as "make it light"; a moon on a light screen reads as "make
  // it dark". Showing current state instead is the common mistake and it leaves
  // the user guessing whether the glyph is a status or a button.
  const label = isDark ? 'Switch to light appearance' : 'Switch to dark appearance'

  const icon = isDark ? (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="4" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M10 1v2M10 17v2M1 10h2M17 10h2M3.6 3.6l1.4 1.4M15 15l1.4 1.4M16.4 3.6L15 5M5 15l-1.4 1.4" />
      </g>
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <path d="M16.5 12.4A7 7 0 0 1 7.6 3.5a7 7 0 1 0 8.9 8.9z" fill="currentColor" />
    </svg>
  )

  // ★ A NATIVE <button>, not an `sgds-button`. The SGDS element renders its own inner shadow
  // <button> and does not forward `aria-label` to it, so the control had NO accessible name on
  // every page (a role=button lookup by name found nothing). The native element names itself.
  return (
    <>
      <button type="button" className="pt-icon-button" onClick={toggleAppearance} aria-label={label} title={label}>
        {icon}
      </button>
      <span aria-live="polite" className="sr-only">
        {isDark ? 'Dark appearance' : 'Light appearance'}
      </span>
    </>
  )
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * What a URL segment is called in the breadcrumb: its registered label, "This record" for an opaque identifier (a
 * raw UUID in the trail read as a bug in the UI review of 2026-10-14), or the segment itself.
 */
export function crumbLabel(segment: string): string {
  return CRUMB_LABEL[segment] ?? (UUID_SEGMENT.test(segment) ? 'This record' : segment)
}

export interface Crumb {
  readonly label: string
  /** A path an officer can open, or null for a path word with no page of its own (`administration`, `mso`). */
  readonly to: string | null
}

const NAV_PATHS: ReadonlySet<string> = new Set(NAV_LINKS.map((link) => link.to))

/**
 * The trail for a path. `versions` is a path word that sits between a rule and one of its versions: it is dropped, so
 * the trail reads Administration > MSO pro-ration rules > MSO-R1 v2 and not "... > Version > This record". The last
 * crumb is the name the screen registered for itself, when it registered one (UAT re-run F-08).
 */
export function crumbsFor(pathname: string, registered: CrumbRegistration | null): readonly Crumb[] {
  const segments = pathname.split('/').filter((s) => s.length > 0)
  const crumbs: Crumb[] = []
  segments.forEach((segment, index) => {
    if (segment === 'versions' && index < segments.length - 1) return
    const path = '/' + segments.slice(0, index + 1).join('/')
    const isLast = index === segments.length - 1
    const label = isLast && registered !== null && registered.path === pathname ? registered.label : crumbLabel(segment)
    crumbs.push({ label, to: isLast ? null : NAV_PATHS.has(path) ? path : null })
  })
  return crumbs
}

/**
 * ★ A real `<nav>` with an ordered list, not `SgdsBreadcrumb`. The SGDS element threw an uncaught
 * `TypeError ... reading 'cloneNode'` from `_replaceExcessItemsWithDropdown` on every rule version page and, at
 * 390 px, collapsed the trail to fragments ("Admir", "Ve", "This re"). This one wraps instead of collapsing, is a
 * navigation landmark with a name, and marks the current page for assistive technology.
 */
function Breadcrumbs({ registered }: { registered: CrumbRegistration | null }) {
  const location = useLocation()
  const crumbs = crumbsFor(location.pathname, registered)
  return (
    <nav aria-label="Breadcrumb" className="pt-crumbs">
      <ol>
        <li>
          {/*
            ⚠ `Link`, for the same reason as the rail. As a `NavLink` this marked itself `aria-current="page"` on
            /dashboard at the same moment the rail's own link did.
          */}
          {crumbs.length === 0 ? <span aria-current="page">Home</span> : <Link to="/dashboard">Home</Link>}
        </li>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1
          return (
            <li key={crumb.label + index}>
              {crumb.to !== null ? (
                <Link to={crumb.to}>{crumb.label}</Link>
              ) : (
                <span {...(isLast && { 'aria-current': 'page' as const })}>{crumb.label}</span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** The user's own open/closed choices for rail groups, remembered between visits. */
export const RAIL_STATE_KEY = 'prs2.rail.groups'

function readRailChoices(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(RAIL_STATE_KEY)
    if (raw === null) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    const out: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(parsed)) if (typeof value === 'boolean') out[key] = value
    return out
  } catch {
    // Storage unavailable or corrupt: the rail still works, it just forgets.
    return {}
  }
}

function writeRailChoices(choices: Record<string, boolean>): void {
  try {
    localStorage.setItem(RAIL_STATE_KEY, JSON.stringify(choices))
  } catch {
    // Not remembering is acceptable; failing the click is not.
  }
}

const RAIL_PANEL_ID = 'pt-rail-panel'
const railGroupPanelId = (group: NavGroup): string => `${groupHeadingId(group)}-panel`

export function Shell() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // Phone only: the whole rail sits behind one "Menu" disclosure (CSS keeps it open from lg up).
  const [menuOpen, setMenuOpen] = useState(false)
  // Only the groups the user has explicitly opened or closed. Absent = the default rule below.
  const [choices, setChoices] = useState<Record<string, boolean>>(readRailChoices)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  // The name the current screen gave itself (rule and version, calculation id); see lib/crumbs.tsx.
  const [registered, setRegistered] = useState<CrumbRegistration | null>(null)
  const crumbContext = useMemo(() => ({ set: setRegistered }), [])

  // Following a link closes the phone menu.
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // ★ The rail this session may actually reach. See NAV_LINKS' own header for
  // why the predicate is the SERVER'S, imported rather than restated.
  //
  // ⚠ `user` is `undefined` for the moment between mount and the session
  // resolving. Showing NOTHING then is the safe direction: a rail that renders
  // every link and then removes eleven of them a tick later has already shown
  // the map it exists to withhold, and it flickers.
  const scope = user?.scope
  const visibleLinks = scope === undefined ? [] : NAV_LINKS.filter((link) => link.visibleTo?.(scope) ?? true)

  /**
   * ★★★ WAVE 24 — THE ONE LINK THAT IS THE CURRENT PAGE. Exactly one, or none.
   *
   * This was `location.pathname.startsWith(link.to)`, evaluated per link. On
   * `/audit/archive` BOTH `/audit` and `/audit/archive` are prefixes, so **two
   * links carried `aria-current="page"` and both highlighted** — same again on
   * `/audit/retention`. `aria-current="page"` means *this is the page you are
   * on*; two of them is a contradiction, and a screen reader announces "current
   * page" twice in one rail.
   *
   * Two defects in one expression, and the second is the one a later tidy-up
   * would have reintroduced:
   *
   *   1. Every ancestor matched, not just the deepest. Fixed by picking the
   *      LONGEST match — the most specific link wins, which is also what a
   *      reader means by "where am I".
   *   2. `startsWith` ignores segment boundaries. `/audit` would match a future
   *      `/auditing`, highlighting an unrelated screen. Fixed by requiring an
   *      exact match or a `/` immediately after — done now, while the rule is
   *      being written, rather than after someone adds the route that exposes it.
   */
  const activeTo = visibleLinks
    .filter((link) => location.pathname === link.to || location.pathname.startsWith(`${link.to}/`))
    .reduce<string | null>((best, link) => (best === null || link.to.length > best.length ? link.to : best), null)

  // Default open: Operations, and whichever group holds the page the officer is on. A choice the
  // officer has made always wins over the default.
  const activeGroup = visibleLinks.find((link) => link.to === activeTo)?.group
  const isGroupOpen = (group: NavGroup): boolean => choices[group] ?? (group === 'Operations' || group === activeGroup)
  // Keep the current page's link visible inside the rail's own scroll box.
  //
  // ★ F-02: this used to call `scrollIntoView` on the link at every route. Scrolling to an element in the rail
  // moves the browser's sequential-focus start point there, so the first Tab on every page landed on the link AFTER the
  // current one and the "Skip to main content" link was three Shift+Tabs behind. The rail's own box is now scrolled
  // directly (`scrollTop`), which moves nothing else, and only when the link is actually out of view.
  useEffect(() => {
    const box = document.querySelector<HTMLElement>('.pt-rail-scroll')
    const link = document.querySelector<HTMLElement>('.pt-rail [aria-current="page"]')
    if (box === null || link === null || box.scrollHeight <= box.clientHeight) return
    const delta = link.getBoundingClientRect().top - box.getBoundingClientRect().top
    if (delta < 0) box.scrollTop += delta
    else if (delta + link.offsetHeight > box.clientHeight) box.scrollTop += delta + link.offsetHeight - box.clientHeight
  }, [activeTo])

  // ★ F-22: the browser tab names the screen ("MSO calculator | PRS2.0"), and a screen reader reads that on arrival.
  const crumbs = crumbsFor(location.pathname, registered)
  const screenName = crumbs.length === 0 ? 'Processing overview' : (crumbs[crumbs.length - 1] as Crumb).label
  useEffect(() => {
    document.title = pageTitle(screenName)
  }, [screenName])

  // ★ F-03: on a ROUTE CHANGE (not on first load, so the first Tab still reaches "Skip to main content") focus moves
  // to the new screen's heading, which a screen reader reads as the page change. Without it focus was dropped to
  // the top of the document and the officer had to Tab through the whole rail again.
  // ⚠ Compared with the PREVIOUS path, not a "first run" flag: StrictMode runs every effect twice on mount, which
  // spent a first-run flag on the first pass and moved focus on load.
  const lastPath = useRef(location.pathname)
  useEffect(() => {
    if (lastPath.current === location.pathname) return
    lastPath.current = location.pathname
    const main = document.getElementById('pt-main')
    if (main === null) return
    const heading = main.querySelector<HTMLElement>('h1')
    const target = heading ?? main
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
    target.focus({ preventScroll: true })
  }, [location.pathname])
  const toggleGroup = (group: NavGroup) => {
    const next = { ...choices, [group]: !isGroupOpen(group) }
    setChoices(next)
    writeRailChoices(next)
  }

  // Escape closes the phone menu and hands focus back to the button that opened it.
  const handleRailKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && menuOpen) {
      setMenuOpen(false)
      menuButtonRef.current?.focus()
    }
  }

  const handleLogout = async () => {
    await logout()
    // ★ ADR 0015 — a page that says so and waits, never a redirect straight back into single sign-on.
    navigate('/signed-out', { replace: true })
  }

  return (
    <CrumbContext.Provider value={crumbContext}>
    <div className="min-h-screen">
      <a href="#pt-main" className="pt-skip-link">
        Skip to main content
      </a>

      {/* The standard ".gov.sg" official-website recognition bar SGDS ships. */}
      <SgdsMasthead fluid={false} />

      <header className="pt-brandband">
        {/*
          ★★ WAVE 25 — THE AUTHORITY'S OWN MARK, on the band that names them.
          `AgencyMark` says what the artwork is and how it is served; the same
          component sits on the sign-in screen's band, so the two cannot drift.
        */}
        <AgencyMark />
        <span className="pt-wordmark">PRS2.0</span>
        <span className="pt-divider" aria-hidden="true" />
        <span className="text-sm opacity-90">Public Service Division</span>
      </header>

      <div className="flex flex-col lg:flex-row items-start gap-6 px-4 sm:px-6 py-6 max-w-[1440px] mx-auto lg:overflow-visible overflow-x-clip">
        {/*
          OPERATIONS / ADMINISTRATION are group HEADINGS, and until now that was
          true for sighted users only: `index.css` styled `.pt-rail-heading` as a
          heading while the markup was a bare `<p>` inside a `<div>` of loose
          links. A screen-reader user tabbing the rail heard five links in a flat
          run with no indication that two sections existed at all.

          Three things carry the structure now, and each does a different job:
            · `<h2>` gives the label real heading semantics, so it appears in a
              screen reader's heading list and can be jumped to directly.
            · `role="group"` + `aria-labelledby` binds the links to that label,
              so entering the group announces "Operations, group" — a heading
              alone marks a position in the document but does not say where the
              group ends or what the links belong to.
            · `<ul role="list">` restores the list semantics Tailwind's preflight
              removes with `list-style: none` (Safari/VoiceOver drops the role
              when the marker is removed), so the count — "list, 4 items" — is
              announced too.

          Heading level: `h2` against the page's own `h1` inside `<main>`. The
          rail precedes `<main>` in the DOM, so the first heading encountered is
          an `h2`; that is a level DECREASE into the `h1`, which the WAI heading
          -order guidance permits (it is skipped levels going down that break
          the outline). The page still has exactly one `h1`.
        */}
        <nav className="pt-rail w-full lg:w-60 lg:shrink-0 lg:sticky lg:top-6 pt-no-print" aria-label="Main" onKeyDown={handleRailKeyDown}>
          {/*
            PHONE: one "Menu" disclosure instead of seven clipped scrollers. From lg up the button is
            hidden and the panel is always shown (CSS), so the desktop rail is unchanged by it.
          */}
          <button
            type="button"
            ref={menuButtonRef}
            className="pt-rail-menu-button lg:hidden"
            aria-expanded={menuOpen}
            aria-controls={RAIL_PANEL_ID}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span>Menu</span>
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false" className="pt-rail-chevron">
              <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div id={RAIL_PANEL_ID} className={`pt-rail-scroll ${menuOpen ? 'mt-3' : 'hidden'} lg:block lg:mt-0`}>
            {/*
              ⚠ A GROUP WITH NO VISIBLE LINKS IS NOT RENDERED AT ALL — heading
              included. An empty "Administration" heading is a worse answer than
              no heading: it announces "list, 0 items" to a screen reader and
              tells a sighted user there is a section here they cannot see into,
              which is precisely the map the gate exists to withhold.

              Each heading is an h2 holding a real <button> (WAI-ARIA accordion): Enter and Space
              operate it natively, `aria-expanded` says the state and `aria-controls` names the
              list. A collapsed list keeps its links in the DOM with `hidden`, which removes them
              from the accessibility tree and the tab order without losing them.
            */}
            {NAV_GROUPS.filter((group) => visibleLinks.some((link) => link.group === group)).map((group) => {
              const open = isGroupOpen(group)
              return (
                <div key={group} role="group" aria-labelledby={groupHeadingId(group)} className="pt-rail-group">
                  <h2 id={groupHeadingId(group)} className="pt-rail-heading">
                    <button
                      type="button"
                      className="pt-rail-toggle"
                      aria-expanded={open}
                      aria-controls={railGroupPanelId(group)}
                      onClick={() => toggleGroup(group)}
                    >
                      <span>{group}</span>
                      <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false" className="pt-rail-chevron">
                        <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </h2>
                  <ul id={railGroupPanelId(group)} role="list" hidden={!open} className="m-0 p-0 list-none">
                    {visibleLinks.filter((link) => link.group === group).sort((a, b) => navRank(a.to) - navRank(b.to)).map((link) => {
                      const active = link.to === activeTo
                      return (
                        <li key={link.to} className="flex">
                          {/*
                            ⚠★ A PLAIN `Link`, NOT a `NavLink`: `NavLink` computes `aria-current`
                            itself and marks every ancestor of the current path, which overrode the
                            longest-match calculation above (two links "current" on /audit/archive).
                            `end` is no fix: /bills must stay marked on /bills/PRS-2026-000123.
                          */}
                          <Link to={link.to} className="pt-rail-link flex-1" aria-current={active ? 'page' : undefined}>
                            {link.label}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center gap-4 flex-wrap mb-4 pt-no-print">
            <div className="flex-1" />
            <AppearanceToggle />
            {user !== null && (
              <div className="flex items-center gap-3 text-sm">
                <div className="text-right">
                  <div className="font-semibold">{user.displayName}</div>
                  <div className="text-[var(--sgds-color-subtle)]">{ROLE_LABELS[user.scope.role]}</div>
                </div>
                {user.canSwitchUser === true && (
                  <SgdsButton variant="outline" size="sm" type="button" onClick={() => navigate('/assume')}>
                    Switch user
                  </SgdsButton>
                )}
                <SgdsButton variant="ghost" size="sm" type="button" onClick={handleLogout}>
                  Sign out
                </SgdsButton>
              </div>
            )}
          </div>

          <div className="mb-4 pt-no-print">
            <Breadcrumbs registered={registered} />
          </div>

          <main id="pt-main">
            <Outlet />
          </main>
        </div>
      </div>

      {/* SGDS requires Privacy and Terms of Use on EVERY page, so the footer belongs to
          the shell rather than to any one page. Outside the two-column grid, so it spans
          the full width under both the rail and the working area. */}
      <GovFooter />
    </div>
    </CrumbContext.Provider>
  )
}
